import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { useRouter } from 'next/router';

import useSearchQuery from '@shared/v2/hooks/useSearchQuery';
import useToggle from '@shared/v2/hooks/useToggle';

type UseFlightOptionsProps = {
  roomId?: string;
};

export type FlightModalContextValue = {
  setOn: () => void;
  setOff: () => void;
  value: boolean;
};

const FlightModalContext = createContext<FlightModalContextValue>({
  setOn: () => {},
  setOff: () => {},
  value: false,
});

const FlightModalProvider = ({ children }: { children: ReactNode }) => {
  const toggleState = useToggle(false);

  return (
    <FlightModalContext.Provider value={toggleState}>
      {children}
    </FlightModalContext.Provider>
  );
};

// We push the params(instead of replace) to the history stack so that,
// when a user clicks on the back button, (when the modal is open),
// they will go back to the package page.
// NOTE: It's also important that when a user on the checkout page clicks back,
// they come to the package page and NOT the flight modal.
// We do NOT automatically open the Flight Modal based on URL param;
// it will only open when a user clicks on a button.
const useFlightModal = ({ roomId }: UseFlightOptionsProps = {}) => {
  const router = useRouter();
  const { setOff, setOn, value: isOpen } = useContext(FlightModalContext);
  const { setSearchQuery } = useSearchQuery();

  const openFlightModal = useCallback(
    async (options?: { offerId?: string }) => {
      if (!options?.offerId || !roomId) return;

      await setSearchQuery(
        {
          room: roomId,
          offerId: options.offerId,
        },
        { push: true },
      );

      setOn();
    },
    [roomId, setOn, setSearchQuery],
  );

  const closeFlightModal = useCallback(async () => {
    await setSearchQuery(
      {
        offerId: undefined,
      },
      { push: true },
    );

    setOff();
  }, [setOff, setSearchQuery]);

  useEffect(() => {
    // Updates the state for scenario where user
    // navigates back from the flight modal.
    if (isOpen && !router.asPath.includes('offerId')) {
      setOff();
    }
  }, [router.asPath, isOpen]);
  return {
    openFlightModal,
    closeFlightModal,
    isFlightModalOpen: isOpen,
  };
};

export { FlightModalProvider, useFlightModal };
